import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/config/firebase'

export type RepairUserRecord = {
  id: string
  name?: string
  email?: string
  phone?: string
  /** @deprecated Prefer `shop_ids`; kept for legacy docs / display */
  shop_id?: string
  shop_ids?: string[]
  createdAt?: Timestamp | Date | string
  updatedAt?: Timestamp | Date | string
}

export function normalizeRepairUserShopIds(r: RepairUserRecord): string[] {
  const fromArr = Array.isArray(r.shop_ids)
    ? r.shop_ids.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : []
  if (fromArr.length > 0) return [...new Set(fromArr)]
  if (r.shop_id && typeof r.shop_id === 'string' && r.shop_id.length > 0) return [r.shop_id]
  return []
}

export async function fetchRepairUsers(): Promise<RepairUserRecord[]> {
  const repairUsersCollection = collection(db, 'repair_users')
  const repairUsersQuery = query(repairUsersCollection, orderBy('createdAt', 'desc'))
  const snapshot = await getDocs(repairUsersQuery)

  return snapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...(docSnapshot.data() as Omit<RepairUserRecord, 'id'>),
  }))
}

export async function upsertRepairUserDocument(params: {
  userId: string
  name: string
  email: string
  phone: string
  shopIds: string[]
}): Promise<void> {
  const shopIds = [...new Set(params.shopIds.filter((id) => id && id.trim().length > 0))]
  if (shopIds.length === 0) {
    throw new Error('At least one repair shop is required')
  }
  const repairUserRef = doc(db, 'repair_users', params.userId)
  const existingRepairSnap = await getDoc(repairUserRef)
  await setDoc(
    repairUserRef,
    {
      id: params.userId,
      name: params.name.trim(),
      email: params.email,
      phone: params.phone,
      shop_ids: shopIds,
      shop_id: shopIds[0],
      updatedAt: Timestamp.now(),
      ...(!existingRepairSnap.exists() ? { createdAt: Timestamp.now() } : {}),
    },
    { merge: true },
  )
}

export async function deleteRepairUserDocument(userId: string): Promise<void> {
  await deleteDoc(doc(db, 'repair_users', userId))
}

export function useRepairUsers() {
  const { data: repairUsers = [] } = useQuery({
    queryKey: ['repair_users'],
    queryFn: fetchRepairUsers,
  })

  const repairUsersById = useMemo(
    () =>
      new Map(
        repairUsers.map((repairUser) => [
          repairUser.id,
          {
            shop_ids: normalizeRepairUserShopIds(repairUser),
            shop_id: repairUser.shop_id || '',
            name: repairUser.name || '',
          },
        ]),
      ),
    [repairUsers],
  )

  return { repairUsers, repairUsersById }
}
